import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Phone, Trash2, Save } from 'lucide-react';
import { PhoneNumber } from '@/types/phoneNumber';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PhoneNumberConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: PhoneNumber | null;
  onUpdate: () => void;
}

export function PhoneNumberConfigModal({ 
  isOpen, 
  onClose, 
  phoneNumber,
  onUpdate 
}: PhoneNumberConfigModalProps) {
  const [friendlyName, setFriendlyName] = useState('');
  const [smsUrl, setSmsUrl] = useState('');
  const [voiceUrl, setVoiceUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  // Initialize form when phone number changes
  React.useEffect(() => {
    if (phoneNumber) {
      setFriendlyName(phoneNumber.friendlyName || '');
      setSmsUrl(phoneNumber.smsUrl || '');
      setVoiceUrl(phoneNumber.voiceUrl || '');
    }
  }, [phoneNumber]);

  const handleSave = async () => {
    if (!phoneNumber) return;

    setSaving(true);
    
    try {
      const { error } = await supabase
        .from('phone_numbers')
        .update({
          friendly_name: friendlyName.trim() || null,
          sms_url: smsUrl.trim() || null,
          voice_url: voiceUrl.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', phoneNumber.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Phone number configuration updated successfully!',
      });

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Configuration update failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update configuration',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!phoneNumber) return;
    
    if (!confirm(`Are you sure you want to delete ${phoneNumber.phoneNumber}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    
    try {
      const { error } = await supabase
        .from('phone_numbers')
        .delete()
        .eq('id', phoneNumber.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Phone number deleted successfully!',
      });

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Phone number deletion failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete phone number',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (!phoneNumber) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Configure Phone Number
          </DialogTitle>
          <DialogDescription>
            Update settings for {phoneNumber.phoneNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Phone Number Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Phone Number Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Phone Number</Label>
                  <div className="text-sm font-mono bg-muted/50 p-2 rounded mt-1">
                    {phoneNumber.phoneNumber}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Provider</Label>
                  <div className="mt-1">
                    <Badge variant="outline">
                      {phoneNumber.provider.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Capabilities</Label>
                  <div className="flex gap-1 mt-1">
                    {phoneNumber.capabilities.map((capability) => (
                      <Badge key={capability} variant="secondary" className="text-xs">
                        {capability.toUpperCase()}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge variant={phoneNumber.status === 'active' ? 'default' : 'secondary'}>
                      {phoneNumber.status.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Form */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Configuration</CardTitle>
              <CardDescription>
                Update the display name and webhook URLs for this phone number
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="friendlyName">Friendly Name</Label>
                <Input
                  id="friendlyName"
                  placeholder="e.g., Main Business Line"
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smsUrl">SMS Webhook URL</Label>
                <Input
                  id="smsUrl"
                  type="url"
                  placeholder="https://your-domain.com/sms-webhook"
                  value={smsUrl}
                  onChange={(e) => setSmsUrl(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="voiceUrl">Voice Webhook URL</Label>
                <Input
                  id="voiceUrl"
                  type="url"
                  placeholder="https://your-domain.com/voice-webhook"
                  value={voiceUrl}
                  onChange={(e) => setVoiceUrl(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving || deleting}
              className="flex items-center gap-2"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Delete Number
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving || deleting}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || deleting}>
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}